/**
 * NotificationBell — Центр внимания в топбаре.
 *
 * Панель отвечает на два вопроса и ни на один больше: что от меня нужно
 * прямо сейчас и что изменилось. Полный разбор — во вкладке «Уведомления»
 * на /inbox; здесь только превью и быстрый триаж.
 *
 * Число на колокольчике растят действия, упоминания и critical. Новые FYI
 * дают тихую точку: счётчик «27 непрочитанных» перестаёт быть сигналом
 * ровно в тот момент, когда в него сваливают всё подряд.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Archive, ArrowRight, Bell, CheckCheck, X } from 'lucide-react'

import { API_BASE, getToken } from '../../api/client'
import { notifications as notificationsApi } from '../../api/notifications'
import { openNotificationTarget } from '../../utils/notificationTarget'
import NotificationCard from '../notifications/NotificationCard'
import {
    bellState, shouldInterrupt, sortAttention, sortRecent, splitByAttention, summarizeLocally,
} from './notificationView'

const POLL_INTERVAL_MS = 30000
const TOAST_MS = 5000
const PREVIEW_LIMIT = 6

export default function NotificationBell() {
    const navigate = useNavigate()
    const location = useLocation()
    const [open, setOpen] = useState(false)
    const [items, setItems] = useState([])
    const [summary, setSummary] = useState(null)
    const [toast, setToast] = useState(null)
    const [snoozeFor, setSnoozeFor] = useState(null)
    const rootRef = useRef(null)
    const wsRef = useRef(null)
    const toastTimerRef = useRef(null)

    // Сводку считает сервер; локальный пересчёт нужен только между ответами,
    // чтобы клик «прочитано» гасил цифру сразу, а не через 30 секунд.
    const badge = useMemo(() => bellState(summary || summarizeLocally(items)), [summary, items])

    const { action, updates } = useMemo(() => splitByAttention(items), [items])
    const actionCards = useMemo(() => sortAttention(action), [action])
    const updateCards = useMemo(() => sortRecent(updates), [updates])

    const showToast = useCallback((n) => {
        if (!shouldInterrupt(n)) return
        setToast(n)
        clearTimeout(toastTimerRef.current)
        toastTimerRef.current = setTimeout(() => setToast(null), TOAST_MS)
    }, [])

    const load = useCallback(async () => {
        try {
            const resp = await notificationsApi.list({ limit: 50 })
            if (!resp?.items) return
            if (resp.summary) setSummary(resp.summary)
            setItems(prev => {
                const known = new Set(prev.map(n => n.id))
                const arrived = resp.items.find(n => !n.is_read && !known.has(n.id))
                if (arrived && prev.length > 0) showToast(arrived)
                return resp.items
            })
        } catch { /* offline — покажем то, что уже есть */ }
    }, [showToast])

    useEffect(() => { load() }, [load])

    // Realtime — главный канал; polling ниже остаётся страховкой на разрыв.
    useEffect(() => {
        const token = getToken()
        if (!token) return undefined

        const wsBase = API_BASE.replace('https://', 'wss://').replace('http://', 'ws://')
        const ws = new WebSocket(`${wsBase}/notifications/ws?token=${encodeURIComponent(token)}`)

        ws.onmessage = (event) => {
            try {
                const payload = JSON.parse(event.data)
                if (payload?.type !== 'notification_new' || !payload.notification) return
                const next = payload.notification
                setItems(prev => (prev.some(n => n.id === next.id) ? prev : [next, ...prev]))
                setSummary(null) // пересчитаем локально до следующего ответа сервера
                showToast(next)
                try {
                    navigator.serviceWorker?.controller?.postMessage({ type: 'mark_seen', tag: String(next.id) })
                } catch { /* SW ещё не активен */ }
            } catch { /* битый payload */ }
        }

        wsRef.current = ws
        return () => {
            try { ws.close(1000, 'NotificationBell unmount') } catch { /* noop */ }
            wsRef.current = null
        }
    }, [showToast])

    useEffect(() => {
        const interval = setInterval(load, POLL_INTERVAL_MS)
        return () => clearInterval(interval)
    }, [load])

    useEffect(() => () => clearTimeout(toastTimerRef.current), [])

    useEffect(() => {
        function handler(e) {
            if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false)
        }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [])

    useEffect(() => { setOpen(false); setSnoozeFor(null) }, [location.pathname])

    // ── Действия над карточкой ──────────────────────────────────

    const markRead = useCallback(async (n) => {
        if (n.is_read) return
        setItems(prev => prev.map(x => (x.id === n.id ? { ...x, is_read: true } : x)))
        setSummary(null)
        try { await notificationsApi.markRead(n.id) } catch { /* offline */ }
    }, [])

    const archive = useCallback(async (n) => {
        setItems(prev => prev.filter(x => x.id !== n.id))
        setSummary(null)
        setSnoozeFor(null)
        try { await notificationsApi.archive(n.id) } catch { /* offline */ }
    }, [])

    const snooze = useCallback(async (n, until) => {
        setItems(prev => prev.filter(x => x.id !== n.id))
        setSummary(null)
        setSnoozeFor(null)
        try { await notificationsApi.snooze(n.id, until) } catch { /* offline */ }
    }, [])

    const openCard = useCallback((n) => {
        // Переход — ПЕРВЫМ действием, до отметки о прочтении. `markRead` ждёт
        // сеть, а после await браузер уже не считает открытие вкладки реакцией
        // на клик: внешняя ссылка на заявку в CRM молча гасится блокировщиком
        // всплывающих окон (Safari, iOS-PWA, WebView).
        if (n.action_url) openNotificationTarget(n.action_url, navigate)
        markRead(n)
        if (n.action_url) setOpen(false)
    }, [markRead, navigate])

    const markAllRead = useCallback(async () => {
        setItems(prev => prev.map(n => ({ ...n, is_read: true })))
        setSummary(null)
        try { await notificationsApi.markAllRead() } catch { /* offline */ }
    }, [])

    const clearRead = useCallback(async () => {
        setItems(prev => prev.filter(n => !n.is_read))
        setSummary(null)
        try { await notificationsApi.archiveRead() } catch { /* offline */ }
    }, [])

    const cardProps = {
        onOpen: openCard,
        onRead: markRead,
        onArchive: archive,
        onSnooze: snooze,
        onToggleSnooze: (n) => setSnoozeFor(prev => (prev === n.id ? null : n.id)),
    }

    return (
        <>
            <button
                className="topbar__bell"
                onClick={() => setOpen(v => !v)}
                aria-label={badge.showCount ? `Требует действия: ${badge.count}` : 'Уведомления'}
            >
                <Bell size={20} />
                {badge.showCount && (
                    <span className={`topbar__bell-badge${badge.critical ? ' topbar__bell-badge--critical' : ''}`}>
                        {badge.label}
                    </span>
                )}
                {badge.showDot && <span className="topbar__bell-dot" aria-hidden="true" />}
            </button>

            {open && (
                <div className="layout-dropdown notif-dropdown" ref={rootRef}>
                    <div className="notif-dropdown__header">
                        <h3 className="notif-dropdown__title flex items-center gap-6"><Bell size={16} /> Центр внимания</h3>
                        <button onClick={() => setOpen(false)} className="notif-dropdown__close" aria-label="Закрыть">
                            <X size={16} />
                        </button>
                    </div>

                    <div className="notif-dropdown__list">
                        {actionCards.length === 0 && updateCards.length === 0 && (
                            <div className="notif-dropdown__empty">Ничего не ждёт вашего внимания</div>
                        )}

                        {actionCards.length > 0 && (
                            <>
                                <div className="notif-section">
                                    Требует действия
                                    <span className="notif-section__count">{actionCards.length}</span>
                                </div>
                                {actionCards.slice(0, PREVIEW_LIMIT).map(n => (
                                    <NotificationCard key={n.id} item={n} snoozeOpen={snoozeFor === n.id} {...cardProps} />
                                ))}
                            </>
                        )}

                        {updateCards.length > 0 && (
                            <>
                                <div className="notif-section">
                                    Обновления
                                    <span className="notif-section__count">{updateCards.length}</span>
                                </div>
                                {updateCards.slice(0, PREVIEW_LIMIT).map(n => (
                                    <NotificationCard key={n.id} item={n} snoozeOpen={snoozeFor === n.id} {...cardProps} />
                                ))}
                            </>
                        )}
                    </div>

                    <div className="notif-dropdown__footer">
                        <div className="notif-dropdown__bulk">
                            <button className="btn btn--secondary btn--sm" onClick={markAllRead}>
                                <CheckCheck size={14} /> Прочитать все
                            </button>
                            <button className="btn btn--secondary btn--sm" onClick={clearRead}>
                                <Archive size={14} /> Убрать прочитанные
                            </button>
                        </div>
                        <button
                            className="btn btn--secondary btn--sm notif-dropdown__more"
                            onClick={() => { setOpen(false); navigate('/inbox?tab=activity') }}
                        >
                            Открыть центр внимания <ArrowRight size={14} />
                        </button>
                    </div>
                </div>
            )}

            {toast && (
                <div
                    onClick={() => { openCard(toast); setToast(null) }}
                    className="notif-toast cursor-pointer flex gap-10 items-start"
                >
                    <span aria-hidden="true" style={{ flexShrink: 0, color: 'var(--color-accent)', display: 'inline-flex' }}>
                        <Bell size={24} />
                    </span>
                    <div className="flex-1 min-w-0">
                        <div className="text-sm fw-700 mb-2">{toast.title}</div>
                        {toast.message && (
                            <div
                                style={{
                                    lineHeight: 1.3,
                                    // text-secondary вместо text-muted: на белом фоне
                                    // контраст 5.9:1 (WCAG AA) против 2.5:1.
                                    color: 'var(--color-text-secondary)',
                                    fontSize: '0.72rem',
                                }}
                            >
                                {toast.message}
                            </div>
                        )}
                    </div>
                    <button
                        onClick={e => { e.stopPropagation(); setToast(null) }}
                        style={{
                            background: 'transparent',
                            border: 'none',
                            padding: 4,
                            borderRadius: 'var(--radius-sm)',
                            display: 'inline-flex',
                        }}
                        aria-label="Закрыть"
                        className="text-muted cursor-pointer flex-shrink-0"
                    >
                        <X size={14} />
                    </button>
                </div>
            )}
        </>
    )
}
